/**
 * AssistantMenu — ⋯ i assistentens topp (#158, PLAN.md steg 8a).
 *
 * «Tidligere samtaler» og «Ny samtale» sto før som to lenkeaktige knapper ved
 * siden av overskriften. Skissen har bare tittelen i toppen, så de to flyttes
 * inn bak ⋯. Det er en vanlig utfoldbar knapp (`aria-expanded`), ikke en
 * ARIA-meny: de to valgene er vanlige knapper i tabulatorrekkefølgen, og det
 * trengs ingen piltaster.
 *
 * For voksne ligger også «Last opp ukeplan» her. Den sto før som en egen rad
 * over feltet, og på telefonen tok den plass fra tråden og utkastene.
 *
 * «Send tilbakemelding» (#187) står sist. Andre skjermer har en knapp i
 * toppen, men her ville den kuttet tittelen på telefonen.
 *
 * Valgene lukker panelet før de gjør noe, og fokus settes på ⋯ først: arket
 * som åpnes, gir fokus tilbake dit det var, og valget selv er borte da. Esc
 * lukker og gir fokus tilbake til ⋯ når fokus var i menyen. Et trykk utenfor,
 * eller fokus som tabber ut av menyen, lukker uten å flytte fokus.
 *
 * Panelet ligger på `z-10` (in-page, Pattern 7): det skal over tråden under
 * toppen, ikke over ark eller bunnmenyen.
 *
 * data-testid: assistant-menu-trigger, assistant-menu,
 *              assistant-conversation-list-trigger, assistant-new-conversation-trigger,
 *              vision-upload-trigger, assistant-feedback-trigger
 */
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { useMenuDismiss } from '../../lib/useMenuDismiss';

interface AssistantMenuProps {
  onOpenConversations: () => void;
  onNewConversation: () => void;
  /** Bare for voksne: «Last opp ukeplan» (VisionUploadSheet). */
  upload?: { onOpen: () => void; pending: boolean; disabled: boolean } | undefined;
  /** «Send tilbakemelding» (#187); utelatt når familien ikke kan sende. */
  onFeedback?: (() => void) | null | undefined;
}

const ITEM =
  'flex min-h-11 w-full items-center gap-3 rounded-sm px-3 text-left text-sm font-bold text-ink hover:bg-inset';

export function AssistantMenu({
  onOpenConversations,
  onNewConversation,
  upload,
  onFeedback,
}: AssistantMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const onBlur = useMenuDismiss(open, setOpen, rootRef, triggerRef);

  function pick(action: () => void) {
    triggerRef.current?.focus();
    setOpen(false);
    action();
  }

  return (
    <div
      ref={rootRef}
      className="relative shrink-0"
      onBlur={onBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid="assistant-menu-trigger"
        aria-label={t('assistant.menu.aria')}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className={`${GLASS_SURFACE} flex h-11 w-11 items-center justify-center rounded-full text-ink`}
      >
        <Icon.More size={20} />
      </button>
      {open ? (
        <div
          id={panelId}
          data-testid="assistant-menu"
          className="absolute right-0 top-full z-10 mt-2 flex w-60 flex-col gap-0.5 rounded-md border border-card-line bg-sheet p-1.5 shadow-md"
        >
          <button
            type="button"
            data-testid="assistant-conversation-list-trigger"
            onClick={() => pick(onOpenConversations)}
            className={ITEM}
          >
            <Icon.Chat size={18} className="shrink-0 text-ink-2" />
            {t('assistant.conversations.title')}
          </button>
          <button
            type="button"
            data-testid="assistant-new-conversation-trigger"
            onClick={() => pick(onNewConversation)}
            className={ITEM}
          >
            <Icon.Plus size={18} className="shrink-0 text-ink-2" />
            {t('assistant.new_conversation')}
          </button>
          {upload ? (
            <button
              type="button"
              data-testid="vision-upload-trigger"
              onClick={() => pick(upload.onOpen)}
              disabled={upload.disabled}
              className={`${ITEM} disabled:pointer-events-none disabled:opacity-40`}
            >
              <Icon.Camera size={18} className="shrink-0 text-ink-2" />
              {upload.pending ? t('assistant.vision.uploading') : t('assistant.vision.uploadCta')}
            </button>
          ) : null}
          {onFeedback ? (
            <button
              type="button"
              data-testid="assistant-feedback-trigger"
              onClick={() => pick(onFeedback)}
              className={ITEM}
            >
              <Icon.Feedback size={18} className="shrink-0 text-ink-2" />
              {t('feedback.fab_label')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
